import React, { useEffect, useMemo, useState } from "react";
import Footer from "../Home/Footer";
import useScrollToTop from "../../hooks/useScrollToTop";
import AuthIcons from "../../hooks/AuthIcons";
import { useUser } from "../../contexts/UserContext";
import AboutMonAppliClub from "./AboutMonAppliClub";
import FormContact from "../Home/FormContact";
import Diaporama from "./Diaporama";
import Features from "./Features";
import LandingButton from "../../components/Button/LandingButton";
import {
  FaArrowRight,
  FaExternalLinkAlt,
  FaPhoneAlt,
  FaUsers,
  FaCalendarAlt,
  FaComments,
} from "react-icons/fa";

const cx = (...c) => c.filter(Boolean).join(" ");

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const MonAppliClub = () => {
  useScrollToTop();
  const { user } = useUser();

  const heroImages = useMemo(
    () => ["/images/illustration_mac.png", "/images/mac_standard_phone.png"],
    [],
  );

  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (typeof window.gtag !== "function") {
      console.warn("Google tag non disponible");
      return;
    }

    window.gtag("event", "conversion", {
      send_to: "AW-18381153899/k61YCJSdjt8cEOvM6LxE",
      value: 1.0,
      currency: "EUR",
    });
  }, []);

  useEffect(() => {
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;
    if (reduceMotion) return;

    const interval = setInterval(() => {
      setIdx((prev) => (prev + 1) % heroImages.length);
    }, 5500);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const handleDemoClick = () => {
    window.open(
      "https://www.monappliclub-demo.com",
      "_blank",
      "noopener,noreferrer",
    );
  };

  const quickLinks = [
    { id: "mac-about", label: "Fonctionnalités" },
    { id: "mac-pages", label: "Aperçus" },
    { id: "mac-features", label: "Sections" },
    { id: "contact", label: "Contact" },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-white dark:bg-slate-950">
      {!user && <AuthIcons />}

      <main className="flex-grow">
        {/* HERO */}
        <section className="relative overflow-hidden border-b border-slate-200 bg-[#eef7fc] dark:border-white/10 dark:bg-slate-950">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.10),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(2,132,199,0.10),transparent_25%)] dark:bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.10),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(6,182,212,0.08),transparent_24%)]" />
          <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-sky-200/20 blur-3xl dark:bg-sky-500/10" />

          <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-24 sm:px-6 sm:pb-20 sm:pt-28 lg:px-8 lg:pb-24 lg:pt-32">
            <div className="grid items-center gap-14 lg:grid-cols-2">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white px-4 py-2 text-sm font-medium text-sky-700 shadow-sm dark:border-sky-500/20 dark:bg-slate-900 dark:text-sky-300">
                  <span className="inline-block h-2 w-2 rounded-full bg-sky-500" />
                  MonAppliClub Standard
                </div>

                <h1 className="mt-6 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl dark:text-white">
                  La plateforme web pour gérer
                  <span className="block text-sky-600 dark:text-sky-400">
                    votre club sportif au quotidien
                  </span>
                </h1>

                <p className="mt-6 text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
                  MonAppliClub centralise les adhérents, inscriptions,
                  paiements, communication, documents, boutique et événements
                  dans une seule interface, accessible selon les rôles.
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <LandingButton variant="primary" onClick={handleDemoClick}>
                    Tester la démo
                  </LandingButton>

                  <LandingButton
                    variant="secondary"
                    onClick={() => scrollToId("mac-pages")}
                  >
                    Voir quelques pages
                  </LandingButton>

                  <LandingButton
                    variant="secondary"
                    onClick={() => scrollToId("contact")}
                  >
                    Me contacter
                  </LandingButton>
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  {[
                    "Sans installation",
                    "Rôles & permissions",
                    "Inscriptions & paiements",
                    "Support visio",
                  ].map((t) => (
                    <span
                      key={t}
                      className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
                  <span>✓ Pensé pour les clubs sportifs</span>
                  <span>✓ Responsive mobile / tablette / PC</span>
                </div>
              </div>

              {/* Mockup */}
              <div className="relative">
                {/* <div className="absolute -left-4 top-10 hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-xl dark:border-white/10 dark:bg-slate-900 lg:block">
                  <div className="flex items-center gap-3">
                    <div className="rounded-xl bg-sky-100 p-3 text-sky-600 dark:bg-sky-500/10 dark:text-sky-400">
                      <FaUsers />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
                        Adhérents
                      </div>
                      <div className="text-lg font-bold text-slate-900 dark:text-white">
                        Gestion centralisée
                      </div>
                    </div>
                  </div>
                </div> */}

                {/* <div className="absolute -right-4 bottom-10 hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-xl dark:border-white/10 dark:bg-slate-900 lg:block">
                  <div className="flex items-center gap-3">
                    <div className="rounded-xl bg-emerald-100 p-3 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400">
                      <FaCalendarAlt />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
                        Événements
                      </div>
                      <div className="text-lg font-bold text-slate-900 dark:text-white">
                        Inscriptions & suivi
                      </div>
                    </div>
                  </div>
                </div> */}

                <div className="relative overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-[0_30px_80px_-20px_rgba(15,23,42,0.20)] dark:border-white/10 dark:bg-slate-900 dark:shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)]">
                  <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-white/10">
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-slate-900 dark:bg-slate-700" />
                      <span className="h-3 w-3 rounded-full bg-orange-400 dark:bg-orange-700" />
                      <span className="h-3 w-3 rounded-full bg-sky-400 dark:bg-sky-700" />
                    </div>
                    <div className="rounded-full bg-slate-100 px-4 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      Aperçu MonAppliClub
                    </div>
                  </div>

                  <div className="p-5 sm:p-6">
                    <div className="overflow-hidden rounded-[26px] border border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-slate-800/70">
                      <img
                        src={heroImages[idx]}
                        alt="Aperçu MonAppliClub"
                        className="h-[260px] w-full object-cover sm:h-[340px]"
                        loading="lazy"
                      />
                    </div>

                    <div className="mt-5 grid grid-cols-2 gap-4">
                      <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-800/80">
                        <div className="flex items-center gap-2 text-sky-600 dark:text-sky-400">
                          <FaUsers />
                          <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
                            Adhérents
                          </span>
                        </div>
                        <div className="mt-3 text-2xl font-bold text-slate-900 dark:text-white">
                          Centralisé
                        </div>
                      </div>

                      <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-800/80">
                        <div className="flex items-center gap-2 text-sky-600 dark:text-sky-400">
                          <FaComments />
                          <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
                            Communication
                          </span>
                        </div>
                        <div className="mt-3 text-2xl font-bold text-slate-900 dark:text-white">
                          Fluide
                        </div>
                      </div>
                    </div>

                    <div className="mt-5 rounded-3xl bg-gradient-to-br from-sky-500 to-cyan-500 p-5 text-white">
                      <div className="text-sm font-medium text-white/80">
                        Ce que gagne votre club
                      </div>
                      <div className="mt-2 text-2xl font-bold sm:text-3xl">
                        Plus d’organisation.
                        <br />
                        Moins de gestion manuelle.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 flex justify-center gap-2">
                  {heroImages.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setIdx(i)}
                      className={cx(
                        "h-2.5 rounded-full transition-all",
                        i === idx
                          ? "w-10 bg-sky-500"
                          : "w-2.5 bg-slate-300 dark:bg-slate-700",
                      )}
                      aria-label={`Image ${i + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* QUICK NAV */}
        <section className="sticky top-20 z-30 border-b border-slate-200 bg-white/85 backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/85">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-2 py-4">
              {quickLinks.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToId(item.id)}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div id="mac-about">
          <AboutMonAppliClub onDemoClick={handleDemoClick} />
        </div>

        <div id="mac-pages">
          <Diaporama />
        </div>

        <div id="mac-features">
          <Features />
        </div>

        {/* CTA */}
        <section className="border-y border-slate-200 bg-slate-50 py-16 dark:border-white/10 dark:bg-slate-900/40">
          <div className="mx-auto max-w-5xl px-4 text-center sm:px-6">
            <div className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-500">
              Passer à l’action
            </div>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl dark:text-white">
              Prêt à découvrir MonAppliClub pour votre structure ?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
              Testez la démo, demandez une présentation ou échangeons simplement
              sur votre besoin.
            </p>

            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <LandingButton variant="primary" onClick={handleDemoClick}>
                Tester la démo
              </LandingButton>

              <LandingButton
                variant="secondary"
                onClick={() => scrollToId("contact")}
              >
                Demander une démo
              </LandingButton>

              <a
                href="tel:0680727707"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-900 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:hover:bg-slate-800"
              >
                <FaPhoneAlt /> 06.80.72.77.07
              </a>
            </div>
          </div>
        </section>

        {/* CONTACT */}
        <section
          id="contact"
          className="bg-white py-16 sm:py-20 dark:bg-slate-950"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-10 max-w-3xl">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-500">
                Contact
              </div>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl dark:text-white">
                Parlons de votre projet
              </h2>
              <p className="mt-4 text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
                Envoyez un message ou réservez directement un créneau.
              </p>
            </div>

            <FormContact />
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MonAppliClub;
